import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { User as UserIcon, Mail, Phone, Lock } from 'lucide-react'
import { PageHero, Button, Input } from '@components/ui'
import { useAuthStore } from '@store/useAuthStore'
import { userService, UserProfile } from '@services/userService'

const roleLabel = (functionId?: number): string => {
  if (functionId === 1) return 'Rececionista'
  if (functionId === 2) return 'Mecânico'
  if (functionId === 3) return 'Administrador'
  return '—'
}

const displayRole = (profile?: UserProfile | null, fallbackUser?: { functionId?: number; function?: number } | null): string => {
  if (profile?.functionName) return profile.functionName
  return roleLabel(profile?.functionId ?? fallbackUser?.functionId ?? fallbackUser?.function ?? undefined)
}

export function ProfilePage() {
  const { user, setUser } = useAuthStore()
  const queryClient = useQueryClient()
  const userId = user?.id

  const { data: profile, isLoading, error } = useQuery(
    ['profile', userId],
    () => userService.getProfile(userId!),
    { enabled: !!userId }
  )

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (profile) {
      setName(profile.name || '')
      setEmail(profile.email || '')
      setPhone(profile.phone || '')
    } else if (user) {
      setName(user.name || '')
      setEmail(user.email || '')
      setPhone('')
    }
  }, [profile, user])

  const updateProfileMutation = useMutation(
    (data: { name: string; email: string; phone?: string; currentPassword?: string; newPassword?: string }) =>
      userService.updateOwnProfile(data),
    {
      onSuccess: (updated) => {
        queryClient.invalidateQueries(['profile', userId])
        setUser?.({
          ...user!,
          name: updated.name,
          email: updated.email,
          phone: updated.phone,
        })
        setSuccessMessage('Dados atualizados com sucesso.')
        setNewPassword('')
        setCurrentPassword('')
        setTimeout(() => setSuccessMessage(null), 4000)
      },
      onError: (err: any) => {
        const msg = err?.response?.data?.error || 'Erro ao atualizar. Tente novamente.'
        setErrorMessage(msg)
        setTimeout(() => setErrorMessage(null), 5000)
      },
    }
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage(null)
    if (!name.trim() || !email.trim()) {
      setErrorMessage('Nome e email são obrigatórios.')
      return
    }
    updateProfileMutation.mutate({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      ...(newPassword ? { currentPassword: currentPassword, newPassword } : {}),
    })
  }

  const displayUser = profile ?? (user ? { ...user, name: user.name, email: user.email, phone: undefined } as Partial<UserProfile> : null)

  if (!userId) {
    return (
      <div className="space-y-8">
        <PageHero title="DADOS PESSOAIS" />
        <div className="rounded-3xl border border-secondary-200 bg-white p-8 text-center text-secondary-500">
          Sessão inválida. Faça login novamente.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <PageHero title="DADOS PESSOAIS" />

      <section className="rounded-3xl border border-secondary-200 bg-white p-6 md:p-8 shadow-sm">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center py-12">
            <p className="text-secondary-500">A carregar dados...</p>
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
            <p className="font-semibold">Não foi possível carregar os seus dados.</p>
            <p className="mt-1 text-sm">Tente novamente mais tarde.</p>
          </div>
        ) : displayUser ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            {successMessage && (
              <div className="rounded-2xl border border-green-200 bg-green-50 p-4 text-green-800">
                {successMessage}
              </div>
            )}
            {errorMessage && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
                {errorMessage}
              </div>
            )}

            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="profile-name" className="block text-xs font-semibold uppercase tracking-[0.3em] text-secondary-400">
                  Nome
                </label>
                <div className="flex items-center gap-2">
                  <UserIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                  <Input
                    id="profile-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nome completo"
                    className="text-lg font-semibold"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="profile-email" className="block text-xs font-semibold uppercase tracking-[0.3em] text-secondary-400">
                  Email
                </label>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary-600 flex-shrink-0" />
                  <Input
                    id="profile-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@exemplo.com"
                    className="text-lg"
                  />
                </div>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <label htmlFor="profile-phone" className="block text-xs font-semibold uppercase tracking-[0.3em] text-secondary-400">
                  Telemóvel
                </label>
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-primary-600 flex-shrink-0" />
                  <Input
                    id="profile-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="912345678"
                    className="text-lg"
                  />
                </div>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <span className="block text-xs font-semibold uppercase tracking-[0.3em] text-secondary-400">Função</span>
                <p className="text-lg font-semibold text-secondary-600">
                  {displayRole(profile, user)}
                </p>
              </div>
            </div>

            <div className="mt-8 space-y-4 border-t border-secondary-200 pt-6">
              <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-secondary-600">
                <Lock className="h-4 w-4" />
                Alterar password
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="profile-current-password" className="mb-1 block text-sm text-secondary-600">
                    Password atual
                  </label>
                  <Input
                    id="profile-current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Deixe em branco para manter"
                    autoComplete="current-password"
                  />
                </div>
                <div>
                  <label htmlFor="profile-new-password" className="mb-1 block text-sm text-secondary-600">
                    Nova password
                  </label>
                  <Input
                    id="profile-new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Deixe em branco para manter"
                    autoComplete="new-password"
                  />
                </div>
              </div>
              {(newPassword || currentPassword) && (
                <p className="text-xs text-secondary-500">
                  Para alterar a password, preencha os dois campos. A password atual é obrigatória.
                </p>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                variant="primary"
                isLoading={updateProfileMutation.isLoading}
                disabled={updateProfileMutation.isLoading}
              >
                Guardar alterações
              </Button>
            </div>
          </form>
        ) : null}
      </section>
    </div>
  )
}
